import { Link } from "react-router-dom";
import { useState } from "react";
import { InstagramLogo, FacebookLogo, LinkedinLogo, XLogo, ArrowRight } from "@phosphor-icons/react";
import { http, formatError } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const QUICK = [
  ["Home","/"], ["About","/about"], ["Investment Opportunities","/investments"],
  ["Real Estate","/real-estate"], ["Agriculture","/agriculture"], ["Strategic Investments","/strategic"],
  ["Events","/events"], ["Partnerships","/partnerships"], ["Insights","/insights"], ["FAQ","/faq"], ["Contact","/contact"],
];
const LEGAL = [
  ["Terms & Conditions","/legal/terms"],
  ["Privacy Policy","/legal/privacy"],
  ["Risk Disclosure","/legal/risk"],
  ["Investment Terms","/legal/investment-terms"],
];

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const { data } = await http.post("/newsletter", { email });
      toast.success(data?.message || "You are now part of the Mayoto community.");
      setEmail("");
    } catch (err) {
      toast.error(formatError(err));
    } finally { setLoading(false); }
  };

  return (
    <footer className="mayo-navy-bg mt-24" data-testid="site-footer">
      <div className="container-wide py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="flex items-center gap-2 mb-5">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 backdrop-blur">
                <span className="text-[#00b4d8] font-black text-2xl leading-none font-display">M</span>
              </span>
              <span className="font-display font-black text-2xl tracking-tight text-white">MAYOTO</span>
            </div>
            <p className="text-[#d4af37] font-display font-bold tracking-[0.25em] text-sm mb-6">INVEST SMART. GROW TOGETHER.</p>
            <p className="text-slate-300 text-sm leading-relaxed max-w-md">
              A Nigerian investment company creating structured opportunities across Real Estate, Agriculture and carefully selected Strategic Business Investments.
            </p>

            <form onSubmit={subscribe} className="mt-8" data-testid="footer-newsletter-form">
              <p className="font-display font-bold text-white mb-3">Join the Mayoto Community</p>
              <div className="flex gap-2 max-w-md">
                <Input
                  type="email" required value={email} onChange={(e)=>setEmail(e.target.value)}
                  placeholder="Your email address"
                  data-testid="footer-newsletter-email"
                  className="bg-white/10 border-white/20 text-white placeholder:text-slate-400 focus-visible:ring-[#00b4d8]"
                />
                <Button type="submit" disabled={loading}
                  data-testid="footer-newsletter-submit"
                  className="btn-shine bg-[#00b4d8] hover:bg-[#0096c7] text-white shrink-0">
                  {loading ? "..." : <>Subscribe <ArrowRight size={16} className="ml-1" /></>}
                </Button>
              </div>
            </form>
          </div>

          <div className="lg:col-span-3">
            <p className="font-display font-bold text-white mb-4 text-sm tracking-widest">QUICK LINKS</p>
            <ul className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
              {QUICK.map(([label, to]) => (
                <li key={to}><Link to={to} className="text-slate-300 hover:text-[#00b4d8] transition-colors">{label}</Link></li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-2">
            <p className="font-display font-bold text-white mb-4 text-sm tracking-widest">LEGAL</p>
            <ul className="flex flex-col gap-2 text-sm">
              {LEGAL.map(([label, to]) => (
                <li key={to}><Link to={to} className="text-slate-300 hover:text-[#00b4d8] transition-colors">{label}</Link></li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-3">
            <p className="font-display font-bold text-white mb-4 text-sm tracking-widest">CONNECT</p>
            <div className="flex gap-3 mb-6">
              <a href="https://instagram.com/mayoto" target="_blank" rel="noreferrer" className="h-10 w-10 rounded-full bg-white/10 hover:bg-[#00b4d8] grid place-items-center transition-colors"><InstagramLogo size={18} weight="bold" /></a>
              <a href="#" className="h-10 w-10 rounded-full bg-white/10 hover:bg-[#00b4d8] grid place-items-center transition-colors"><FacebookLogo size={18} weight="bold" /></a>
              <a href="#" className="h-10 w-10 rounded-full bg-white/10 hover:bg-[#00b4d8] grid place-items-center transition-colors"><LinkedinLogo size={18} weight="bold" /></a>
              <a href="#" className="h-10 w-10 rounded-full bg-white/10 hover:bg-[#00b4d8] grid place-items-center transition-colors"><XLogo size={18} weight="bold" /></a>
            </div>
            <p className="text-sm text-slate-300">Email</p>
            <a href="mailto:mayotonexo@gmail.com" className="text-white hover:text-[#00b4d8] text-sm">mayotonexo@gmail.com</a>
            <p className="text-sm text-slate-300 mt-3">Instagram</p>
            <a href="https://instagram.com/mayoto" className="text-white hover:text-[#00b4d8] text-sm" target="_blank" rel="noreferrer">@mayoto</a>
          </div>
        </div>

        <div className="mt-14 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <p className="text-xs text-slate-400">© {new Date().getFullYear()} Mayoto. All rights reserved. Nigeria.</p>
          <p className="text-xs text-slate-400 max-w-xl">Investments carry risk. Past performance is not indicative of future results. Please read the Risk Disclosure before participating.</p>
        </div>
      </div>
    </footer>
  );
}
